"use client";

import { motion } from "src/lib/motion";
import { AnimatedBeamDemo } from "@/components/magicui/n8n-beam";
import { Check, Cog, LineChart, FileText, Building2, Hospital, ShoppingBag, Factory, Truck, Lightbulb,Earth } from "lucide-react";
import { Icons } from "@/components/icons";
import Section from "@/components/section";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { sanitizeInlineHtml } from "@/lib/sanitize";
import { buttonVariants } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TabSwitchSection from "@/components/ui/tab-switch-section";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useTranslations } from "@/lib/hooks/use-translations";
import { useParams } from "next/navigation";
import { Locale } from "@/lib/i18n";

// Import images
import n8nImage from "@/assets/images/n8n.png";
import dashboardImage from "@/assets/images/dashboard.png";
import businessDiscussionImage from "@/assets/images/business-discussion.jpg";
import scientistImage from "@/assets/images/scientist-working-laboratory-analysing-test-trial-vaccine-checking-drug-data-standing-front-pc.jpg";
import smartphoneOfficeImage from "@/assets/images/smartphone-office.jpg";
import productionLineImage from "@/assets/images/photo-automobile-production-line-welding-car-body-modern-car-assembly-plant-auto-industry.jpg";
import logisticsImage from "@/assets/images/technological-futuristic-holograms-logistics-means-transport.jpg";
import { useRef, useState } from "react";

const ease = [0.16, 1, 0.3, 1];

function Hero() {
  const params = useParams();
  const locale = params.locale as Locale;
  const { t } = useTranslations();

  return (
    <Section className="bg-neutral-100 dark:bg-neutral-900  flex items-center py-8 sm:py-12 md:!py-12">
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="text-center md:text-left order-2 md:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="space-y-3 sm:space-y-4 pb-2"
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
                {t("pages.solutions.automatisation.hero.title")}
              </h1>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-foreground">
                {t("pages.solutions.automatisation.hero.subtitle")}
              </h2>
              <p
                className="text-lg sm:text-xl text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(t("pages.solutions.automatisation.hero.description")) }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8, ease }}
              className="flex flex-col sm:flex-row md:justify-start sm:justify-center w-full gap-3 sm:gap-4 mt-4 sm:mt-6"
            >
              <Link
                href={`/${locale}/contact`}
                className={cn(
                  buttonVariants({ size: "lg" }),
                  "w-full sm:w-auto text-background flex gap-2"
                )}
              >
                <Icons.logoIcon className="h-6 w-6" />
                {t("pages.solutions.automatisation.hero.cta.primary")}
              </Link>
              <button
                onClick={() => document.getElementById("why-automation")?.scrollIntoView({ behavior: "smooth" })}
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "w-full sm:w-auto"
                )}
              >
                {t("pages.solutions.automatisation.hero.cta.secondary")}
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="relative order-1 md:order-2"
          >
            <div className="h-[250px] sm:h-[400px] md:h-[500px]">
              <AnimatedBeamDemo />
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
const SWIPE_THRESHOLD = 50;

function WhyAutomation() {
  const params = useParams();
  const locale = params.locale as Locale;
  const { t } = useTranslations();
  const startX = useRef(0);
  const endX = useRef(0);

  const benefits = t("pages.solutions.automatisation.why.benefits") as Array<{ title: string; content: string }>;

  const data = [
    {
      id: 1,
      title: benefits[0].title,
      content: benefits[0].content,
      image: "/dashboard.png",
      icon: <Cog className="h-6 w-6 text-primary" />,
    },
    {
      id: 2,
      title: benefits[1].title,
      content: benefits[1].content,
      image: "/dashboard.png",
      icon: <LineChart className="h-6 w-6 text-primary" />,
    },
    {
      id: 3,
      title: benefits[2].title,
      content: benefits[2].content,
      image: "/dashboard.png",
      icon: <FileText className="h-6 w-6 text-primary" />,
    },
    {
      id: 4,
      title: benefits[3].title,
      content: benefits[3].content,
      image: "/dashboard.png",
      icon: <Lightbulb className="h-6 w-6 text-primary" />,
    },
    {
      id: 5,
      title: benefits[4].title,
      content: benefits[4].content,
      image: "/dashboard.png",
      icon: <Earth className="h-6 w-6 text-primary" />,
    },
  ];

  const [activeSlide, setActiveSlide] = useState(0);

  const totalSlides = data?.length

  const handlePrevious = () => {
    setActiveSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    endX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = endX.current - startX.current;
    if (diff > SWIPE_THRESHOLD) {
      handlePrevious()
    } else if (diff < -SWIPE_THRESHOLD) {
      handleNext()
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    startX.current = e.clientX;
    endX.current = e.clientX;
    const handleMouseMove = (e: MouseEvent) => {
      endX.current = e.clientX;
    };
    const handleMouseUp = () => {
      const diff = endX.current - startX.current;
      if (diff > SWIPE_THRESHOLD) {
        handlePrevious()
      } else if (diff < -SWIPE_THRESHOLD) {
        handleNext()
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <Section
      id="why-automation"
      mainCta={{
        text: t("pages.solutions.automatisation.hero.cta.primary"),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      secondaryCta={{
        text: t("pages.solutions.automatisation.hero.cta.secondary"),
        onClick: () => document.getElementById("automation-services")?.scrollIntoView({ behavior: "smooth" })
      }}
      largerTopGap
      buttonStyles="lg:mt-9"
    >
      <div className="text-center mb-12">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-4 break-words"
        >
          {t("pages.solutions.automatisation.why.title")}
        </motion.h2>
      </div>

      {/* Benefits Carousel */}

      <div className="relative max-w-4xl mx-auto ">
        <div 
          className="w-full"
          role="region"
          aria-roledescription="carousel"
          aria-label="KI-Beratung Vorteile"
        >
        <div className="overflow-hidden   cursor-grab select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}>
            {data.map((item, index) => (
              index === activeSlide && (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="relative bg-background/50 hover:bg-background/80 transition-colors rounded-xl p-5 sm:p-8 border border-border/50 sm:mx-0"
                  >
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <div className="flex justify-center sm:justify-start sm:mt-1">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          {item.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-4 text-center sm:text-left">{item.title}</h3>
                        <p className="text-base sm:text-lg text-muted-foreground max-md:text-center leading-relaxed">{item.content}</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )
            ))}
          </div>
          
          {/* Navigation controls */}
          <button 
            onClick={handlePrevious}
            className="absolute left-0 sm:left-12 lg:left-0 top-[99%] -translate-y-1/2 sm:-translate-x-12 bg-background/80 border border-primary rounded-full p-2 flex items-center justify-center duration-200 md:hover:bg-primary md:hover:text-white lg:top-[39%]"
            aria-label="Vorheriger Slide"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
              <path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>
          <button 
            onClick={handleNext}
            className="absolute right-0 sm:right-12 lg:right-0 top-[99%] -translate-y-1/2 sm:translate-x-12 bg-background/80 border border-primary rounded-full p-2 flex items-center justify-center duration-200 md:hover:bg-primary md:hover:text-white lg:top-[39%]"
            aria-label="Nächster Slide"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
              <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>
          
          {/* Slide indicator */}
          <div className="my-10  flex justify-center gap-2">
            <div className="flex gap-1">
              {data.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`w-2 h-2 rounded-full ${index === activeSlide ? 'bg-primary' : 'bg-primary/30'}`}
                  aria-label={`Zu Slide ${index + 1} gehen`}
                  aria-current={index === activeSlide ? 'true' : 'false'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Industries() {
  const params = useParams();
  const locale = params.locale as Locale;
  const { t } = useTranslations();

  const categories = t("pages.solutions.automatisation.industries.categories") as Array<{ id: string; label: string }>;
  const contents = t("pages.solutions.automatisation.industries.contents") as Record<string, {
    title: string;
    description: string;
    features: string[];
  }>;

  const industryCategories = [
    { id: "financial", label: categories[0].label, icon: <Building2 className="h-4 w-4" /> },
    { id: "healthcare", label: categories[1].label, icon: <Hospital className="h-4 w-4" /> },
    { id: "retail", label: categories[2].label, icon: <ShoppingBag className="h-4 w-4" /> },
    { id: "manufacturing", label: categories[3].label, icon: <Factory className="h-4 w-4" /> },
    { id: "logistics", label: categories[4].label, icon: <Truck className="h-4 w-4" /> },
  ];

  const industryContents = {
    financial: {
      title: contents.financial.title,
      description: contents.financial.description,
      features: contents.financial.features,
      image: businessDiscussionImage.src,
      url: `https://savagescorp.ai/${locale}/solutions/automatisation/financial`
    },
    healthcare: {
      title: contents.healthcare.title,
      description: contents.healthcare.description,
      features: contents.healthcare.features,
      image: scientistImage.src,
      url: `https://savagescorp.ai/${locale}/solutions/automatisation/healthcare`
    },
    retail: {
      title: contents.retail.title,
      description: contents.retail.description,
      features: contents.retail.features,
      image: smartphoneOfficeImage.src,
      url: `https://savagescorp.ai/${locale}/solutions/automatisation/retail`
    },
    manufacturing: {
      title: contents.manufacturing.title,
      description: contents.manufacturing.description,
      features: contents.manufacturing.features,
      image: productionLineImage.src,
      url: `https://savagescorp.ai/${locale}/solutions/automatisation/manufacturing`
    },
    logistics: {
      title: contents.logistics.title,
      description: contents.logistics.description,
      features: contents.logistics.features,
      image: logisticsImage.src,
      url: `https://savagescorp.ai/${locale}/solutions/automatisation/logistics`
    }
  };

  return (
    <Section id="industries" containerClassName="!px-0" >
      <TabSwitchSection
        title={t("pages.solutions.automatisation.industries.title")}
        subtitle={t("pages.solutions.automatisation.industries.subtitle")}
        categories={industryCategories}
        navigationStyles="lg:mb-24"
        contents={industryContents}
        defaultTab="financial"
        className="!py-0"
      />
    </Section>
  );
}

function CaseStudy() {
  const params = useParams();
  const locale = params.locale as Locale;
  const { t } = useTranslations();

  const benefits = t("pages.solutions.automatisation.case-study.benefits") as string[];

  return (
    <Section
      id="case-study"
      title={t("pages.solutions.automatisation.case-study.title")}
      subtitle={t("pages.solutions.automatisation.case-study.subtitle")}
      className="bg-muted/30"
      containerClassName="!px-0"
      titleBlockClassName="lg:pb-14"
      addButtonGaps
      buttonStyles="lg:mt-16"
      mainCta={{
        text: t("pages.solutions.automatisation.case-study.buttons.getStarted"),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      secondaryCta={{
        text: t("pages.solutions.automatisation.case-study.buttons.readMore"),
        onClick: () => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })
      }}
    >
      <div className="container px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-full rounded-lg overflow-hidden drop-shadow-xl">
                <Image
                  src={n8nImage.src}
                  alt="n8n Workflow Automation Dashboard - Showcasing WooCommerce, Slack and Brevo integration for automated order processing and inventory management"
                  width={600}
                  height={400}
                  className="w-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-lg text-muted-foreground max-md:text-center">
              {t("pages.solutions.automatisation.case-study.description")}
            </p>

            <p className="text-lg text-muted-foreground mt-4 max-md:text-center">
              {t("pages.solutions.automatisation.case-study.result")}
            </p>

            <div className="space-y-4 mt-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="mt-1 bg-primary/10 rounded-full p-1">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

function FAQ() {
  const params = useParams();
  const locale = params.locale as Locale;
  const { t } = useTranslations();

  const faqs = t("pages.solutions.automatisation.faq.items") as Array<{ question: string; answer: string }>;

  return (
    <Section
      id="faq"
      title={t("pages.solutions.automatisation.faq.title")}
      subtitle={t("pages.solutions.automatisation.faq.subtitle")}
      className="bg-muted/30 max-md:!pt-8"
    >
      <div className="mx-auto md:max-w-[800px] lg:my-12">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col items-center justify-center space-y-2"
        >
          {faqs.map((faq, idx) => (
            <AccordionItem
              key={idx}
              value={faq.question}
              className="w-full border rounded-lg overflow-hidden shadow-lg hover:bg-primary/10 transition durantion-200 [&[data-state=open]]:bg-primary/10"
            >
              <AccordionTrigger className="px-4">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="px-4">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}

function FinalCTA() {
  const params = useParams();
  const locale = params.locale as Locale;
  const { t } = useTranslations();

  return (
    <Section
      id="final-cta"
      className="bg-primary/5"
      mainCta={{
        text: t("pages.solutions.automatisation.hero.cta.primary"),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
    >
      <div className="text-center max-w-3xl mx-auto sm:px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-4 break-words"
        >
          {t("pages.solutions.automatisation.final-cta.title")}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-muted-foreground mb-8"
        >
          {t("pages.solutions.automatisation.final-cta.description")}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row flex-wrap justify-center gap-4"
        >
        </motion.div>
      </div>
    </Section>
  );
}

export {
  Hero,
  WhyAutomation,
  Industries,
  CaseStudy,
  FAQ,
  FinalCTA,
};
